// src/components/VariantPickerModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  groupVariantOptions,
  findVariant,
  variantKeyOf,
} from "../utils/variants";

export default function VariantPickerModal({ product, open, onClose, onConfirm }) {
  // ✅ variants mémoïsé pour éviter que sa “forme” change à chaque render
  const variants = useMemo(
    () => (Array.isArray(product?.variants) ? product.variants : []),
    [product]
  );

  // ✅ dérivés mémoïsés à partir de `variants`
  const { colors, sizes } = useMemo(() => groupVariantOptions(variants), [variants]);

  const [color, setColor] = useState(colors[0] || "");
  const [size, setSize] = useState(sizes[0] || "");

  // ✅ réinitialiser les sélections à l’ouverture, en fonction des options courantes
  useEffect(() => {
    if (!open) return;
    setColor(colors[0] || "");
    setSize(sizes[0] || "");
  }, [open, colors, sizes]);

  // ✅ sélection mémoïsée
  const selected = useMemo(
    () => findVariant(variants, { color, size }) || null,
    [variants, color, size]
  );

  const canAdd =
    !!selected && (typeof selected.stock !== "number" || selected.stock > 0);

  const priceXOF = useMemo(
    () => (Number(product?.price || 0)).toFixed(0),
    [product?.price]
  );

  // 🔒 lock page scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* wrapper covers the entire viewport */}
      <div
        className="modal d-block"
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1055,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: 560, width: "100%" }}
        >
          <div className="modal-content shadow">
            <div className="modal-header">
              <h5 className="modal-title">{product?.title || "Choisir la variante"}</h5>
              <button type="button" className="btn-close" aria-label="Fermer" onClick={onClose} />
            </div>

            {/* make only the body scrollable */}
            <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="d-flex gap-3 align-items-start">
                <img
                  src={product?.images?.[0] || "https://via.placeholder.com/140x100?text=Image"}
                  alt=""
                  style={{ width: 140, height: 100, objectFit: "cover", borderRadius: 6 }}
                />
                <div className="flex-grow-1 small text-muted">
                  Voir tous les détails sur la page produit
                </div>
              </div>

              <hr />

              {colors.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Couleur</label>
                  <div className="d-flex flex-wrap gap-2">
                    {colors.map((c) => (
                      <button
                        key={c || "none"}
                        type="button"
                        className={`btn btn-sm ${c === color ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setColor(c)}
                      >
                        {c || "—"}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {sizes.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Taille</label>
                  <select
                    className="form-select"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                  >
                    {sizes.map((s) => (
                      <option key={s || "none"} value={s}>
                        {s || "—"}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-baseline">
                <div>
                  <div className="small text-muted">Prix</div>
                  <div className="fs-4 fw-semibold">{priceXOF} FCFA</div>
                </div>
                <div className="text-end">
                  <div className="small text-muted">Stock</div>
                  <div className={canAdd ? "text-success" : "text-danger"}>
                    {selected ? (selected.stock ?? "—") : "—"}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={onClose}>
                Annuler
              </button>
              <button
                className="btn btn-primary"
                disabled={!canAdd}
                onClick={() => selected && onConfirm(variantKeyOf(selected), selected)}
              >
                Ajouter au panier
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* backdrop fixed & below the modal */}
      <div
        className="modal-backdrop fade show"
        style={{ position: "fixed", inset: 0, zIndex: 1050 }}
        onClick={onClose}
      />
    </>
  );
}
